// Copyright 2025. WebPros International GmbH. All rights reserved.

import React from 'react';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { connect } from 'react-redux';
import { Loader } from 'common/components';
import {
    CodeEditor,
    Section,
    Text,
    Translate,
} from '@plesk/ui-library';
import { Button } from 'admin/common/components/Button/Button';
import { StyledCodeEditor } from 'admin/common/styles/CodeEditor';
import { IDisasterRecoveryEntityResponse } from 'common/api/resources/DisasterRecovery';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as historyActions from 'common/modules/disasterRecovery/actions';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { DISASTER_RECOVERY_HISTORY } from 'admin/disasterRecovery/constants/test';
import { MarginTopBottom } from 'admin/disasterRecovery/components/HistoryTable/Styles';

interface IDisasterRecoveryOriginalData {
    entityIds: number[];
    processIds: number[];
    handleDeletedManually: (entityIds: number[]) => void;
}

export type DisasterRecoveryOriginalDataProps =
    IDisasterRecoveryOriginalData &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const DisasterRecoveryOriginalData: React.FC<DisasterRecoveryOriginalDataProps> = ({
    entityIds,
    processIds,
    entities,
    isLoading,
    isDownloading,
    isSubmittingManuallyDeleted,
    disasterRecoveryHistoryActions: {
        getDisasterRecoveryEntities,
        downloadOriginalData,
    },
    handleDeletedManually,
}) => {
    const [configurations, setConfigurations] = React.useState<string>('');

    const NUMBER_OF_ENTITIES_SHOWN = 20;

    React.useEffect(() => {
        getDisasterRecoveryEntities({
            filters: {
                disaster_recovery_entity_id: entityIds.slice(0, NUMBER_OF_ENTITIES_SHOWN),
                disaster_recovery_id: processIds,
            },
        }, NUMBER_OF_ENTITIES_SHOWN);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const mergedConfigurations = entities.data.map(function (el: IDisasterRecoveryEntityResponse) {
            return JSON.parse(el.original_data);
        });

        setConfigurations(JSON.stringify(mergedConfigurations, null, 2));

    }, [entities]);

    const handleDownloadOriginalData = () => {
        downloadOriginalData({
            disaster_recovery_ids: processIds,
            disaster_recovery_entity_ids: entityIds,
        });
    };

    return (
        <Loader isLoading={isLoading} center={false}>
            <Section>
                {entities.data.length >= NUMBER_OF_ENTITIES_SHOWN && (
                    <MarginTopBottom>
                        <Text
                            fontSize={SIZE.SM}
                            intent={INTENT_TYPE.MUTED}
                        >
                            <Translate content="disasterRecovery.history.actions.viewOriginalData.limit" params={{
                                entities_count: NUMBER_OF_ENTITIES_SHOWN,
                            }} />
                        </Text>
                    </MarginTopBottom>
                )}
                <StyledCodeEditor height={400}>
                    <CodeEditor
                        readOnly={true}
                        mode="json"
                        onChange={(value: string) => value}
                        options={{
                            tabSize: 2,
                            smartIndent: false,
                        }}
                    >
                        {configurations}
                    </CodeEditor>
                </StyledCodeEditor>
                <MarginTopBottom>
                    <Button
                        ghost={true}
                        isLoading={isDownloading}
                        onClick={handleDownloadOriginalData}
                        data-cy={DISASTER_RECOVERY_HISTORY.DOWNLOAD_ORIGINAL_DATA}
                    >
                        <Translate content="disasterRecovery.history.originalDataDialog.downloadLink" />
                    </Button>
                </MarginTopBottom>
            </Section>
            <Button
                intent={INTENT_TYPE.PRIMARY}
                onClick={() => handleDeletedManually(entityIds)}
                ghost={false}
                isLoading={isSubmittingManuallyDeleted}
                data-cy={DISASTER_RECOVERY_HISTORY.DELETE_ORIGINAL_DATA_MANUALLY}
                tooltip={<Translate content="disasterRecovery.history.originalDataDialog.confirmDeletedManually.tooltip" />}
            >
                <Translate content="disasterRecovery.history.originalDataDialog.confirmDeletedManually.button" />
            </Button>
        </Loader>
    );
};

const mapStateToProps = (state: RootState) => ({
    entities: state.disasterRecoveryHistory.entities,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_ENTITIES),
    isDownloading: state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_DOWNLOADING_ORIGINAL_DATA),
    isSubmittingManuallyDeleted: state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_ORIGINAL_DATA_DELETING),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    disasterRecoveryHistoryActions: bindActionCreators(historyActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(DisasterRecoveryOriginalData);
